import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ZoomIn, ArrowRight, MapPin, Camera } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';

// ─── SHEER CURTAINS ──────────────────────────────────────────────────────────
import sc01 from '../assets/portfolio/sc01.jpg';
import sc02 from '../assets/portfolio/sc02.jpg';
import sc03 from '../assets/portfolio/sc03.jpg';
import sc04 from '../assets/portfolio/sc04.jpg';
import sc05 from '../assets/portfolio/sc05.jpg';
import sc06 from '../assets/portfolio/sc06.jpg';

// ─── BLOCKOUT CURTAINS ───────────────────────────────────────────────────────
import bc01 from '../assets/portfolio/bc01.jpg';
import bc02 from '../assets/portfolio/bc02.jpg';
import bc03 from '../assets/portfolio/bc03.jpg';
import bc04 from '../assets/portfolio/bc04.jpg';
import bc05 from '../assets/portfolio/bc05.jpg';

// ─── BLOCKOUT BLINDS ─────────────────────────────────────────────────────────
import bb01 from '../assets/portfolio/bb01.jpg';
import bb02 from '../assets/portfolio/bb02.jpg';
import bb03 from '../assets/portfolio/bb03.jpg';
import bb04 from '../assets/portfolio/bb04.jpg';
import bb05 from '../assets/portfolio/bb05.jpg';

// ─── PLANTATION SHUTTERS ─────────────────────────────────────────────────────
import ps01 from '../assets/portfolio/ps01.jpg';
import ps02 from '../assets/portfolio/ps02.jpg';
import ps03 from '../assets/portfolio/ps03.jpg';
import ps04 from '../assets/portfolio/ps04.jpg';
import ps05 from '../assets/portfolio/ps05.jpg';

// ─── OUTDOOR SHUTTERS ────────────────────────────────────────────────────────
import os01 from '../assets/portfolio/os01.jpg';
import os02 from '../assets/portfolio/os02.jpg';
import os03 from '../assets/portfolio/os03.jpg';

// ─── LINEN CURTAINS (product images) ─────────────────────────────────────────
import linen1 from '../assets/products/linen_1.png';
import linen2 from '../assets/products/linen_2.png';
import linen3 from '../assets/products/linen_3.png';

// ─── CUSTOM CURTAINS (product images) ────────────────────────────────────────
import custom1 from '../assets/products/custom_1.png';
import custom2 from '../assets/products/custom_2.png';
import custom3 from '../assets/products/custom_3.png';

// ─── FEATURED PROJECT (latest install) ───────────────────────────────────────
// Showcased at the top of the page and also listed first in the grid below.
const featuredProject = {
  name:    'Sheer Curtains',
  heading: <>Sheer <em>Curtains</em></>,
  tagline: 'Soft, filtered daylight from one wall to the next',
  story:   'Floor-to-ceiling S-Fold sheers run wall-to-wall through the open-plan living and dining area and continue into the bedroom — softening harsh daylight, keeping privacy from the street and tying the whole home together with one calm, continuous line of fabric.',
  suburb:  'Adelaide SA',
  link:    '/products/sheer-curtains-adelaide',
  specs: [
    { label:'Product', value:'Sheer Curtains' },
    { label:'Heading', value:'S-Fold' },
    { label:'Fit',     value:'Ceiling track, floor to ceiling' },
    { label:'Spaces',  value:'Living, dining & bedroom' },
  ],
  photos: [
    { id:28, img:sc06, room:'Open-Plan Living & Dining' },
    { id:29, img:sc05, room:'Dining Room' },
    { id:30, img:sc04, room:'Bedroom' },
  ],
};

const featuredPhotos = featuredProject.photos.map(p => ({
  ...p,
  cat:    'Sheer Curtains',
  title:  `${featuredProject.name} — ${p.room}`,
  suburb: featuredProject.suburb,
  link:   featuredProject.link,
  isNew:  true,
}));

const portfolioItems = [
  // Latest project
  ...featuredPhotos,
  // Sheer Curtains
  { id:1, cat:'Sheer Curtains',      img:sc01, title:'S-Fold Sheer Curtains — Living Room',      suburb:'Norwood, Adelaide SA',      link:'/products/sheer-curtains-adelaide' },
  { id:2,  cat:'Sheer Curtains',      img:sc02, title:'Sheer Curtains — Master Bedroom',           suburb:'Unley, Adelaide SA',         link:'/products/sheer-curtains-adelaide' },
  { id:3,  cat:'Sheer Curtains',      img:sc03, title:'Floor-Length Sheers — Open Plan Lounge',    suburb:'Glenelg, Adelaide SA',       link:'/products/sheer-curtains-adelaide' },
  // Blockout Curtains
  { id:4,  cat:'Blockout Curtains',   img:bc01, title:'Pinch Pleat Blockout — Formal Lounge',     suburb:'Burnside, Adelaide SA',      link:'/products/blackout-curtains-adelaide' },
  { id:5,  cat:'Blockout Curtains',   img:bc02, title:'Blockout Curtains — Master Suite',          suburb:'North Adelaide SA',          link:'/products/blackout-curtains-adelaide' },
  { id:6,  cat:'Blockout Curtains',   img:bc03, title:'Thermal Blockout — Kids Bedroom',           suburb:'Prospect, Adelaide SA',      link:'/products/blackout-curtains-adelaide' },
  { id:7,  cat:'Blockout Curtains',   img:bc04, title:'S-Fold Blockout — Living Area',             suburb:'Henley Beach, Adelaide SA',  link:'/products/blackout-curtains-adelaide' },
  { id:8,  cat:'Blockout Curtains',   img:bc05, title:'Custom Blockout — Home Office',             suburb:'Mawson Lakes, Adelaide SA',  link:'/products/blackout-curtains-adelaide' },
  // Blockout Blinds
  { id:9,  cat:'Blockout Blinds',     img:bb01, title:'Roller Blinds — Modern Kitchen',            suburb:'Lightsview, Adelaide SA',    link:'/products/blockout-blinds-adelaide' },
  { id:10, cat:'Blockout Blinds',     img:bb02, title:'Blockout Roller Blinds — Bedroom',          suburb:'Semaphore, Adelaide SA',     link:'/products/blockout-blinds-adelaide' },
  { id:11, cat:'Blockout Blinds',     img:bb03, title:'Motorised Blinds — Living Room',            suburb:'Craigburn Farm, Adelaide SA',link:'/products/blockout-blinds-adelaide' },
  { id:12, cat:'Blockout Blinds',     img:bb04, title:'Sunscreen Roller Blinds — Office',          suburb:'Adelaide CBD SA',            link:'/products/blockout-blinds-adelaide' },
  { id:13, cat:'Blockout Blinds',     img:bb05, title:'Dual Roller Blinds — Open Plan',            suburb:'West Lakes, Adelaide SA',    link:'/products/blockout-blinds-adelaide' },
  // Plantation Shutters
  { id:14, cat:'Plantation Shutters', img:ps01, title:'Full Height Plantation Shutters',            suburb:'Stirling, Adelaide Hills SA',link:'/products/plantation-shutters-adelaide' },
  { id:15, cat:'Plantation Shutters', img:ps02, title:'Plantation Shutters — Bay Window',           suburb:'Medindie, Adelaide SA',     link:'/products/plantation-shutters-adelaide' },
  { id:16, cat:'Plantation Shutters', img:ps03, title:'Café Style Shutters — Kitchen',              suburb:'Walkerville, Adelaide SA',  link:'/products/plantation-shutters-adelaide' },
  { id:17, cat:'Plantation Shutters', img:ps04, title:'Bi-Fold Shutters — Bedroom Suite',           suburb:'Springfield, Adelaide SA',  link:'/products/plantation-shutters-adelaide' },
  { id:18, cat:'Plantation Shutters', img:ps05, title:'White Plantation Shutters — Lounge',         suburb:'Toorak Gardens, Adelaide SA',link:'/products/plantation-shutters-adelaide' },
  // Outdoor Shutters
  { id:19, cat:'Outdoor Shutters',    img:os01, title:'Aluminium Outdoor Shutters — Facade',        suburb:'Glenelg South, Adelaide SA',link:'/products/outdoor-shutters-adelaide' },
  { id:20, cat:'Outdoor Shutters',    img:os02, title:'Weather-Proof Shutters — Alfresco Area',     suburb:'Brighton, Adelaide SA',     link:'/products/outdoor-shutters-adelaide' },
  { id:21, cat:'Outdoor Shutters',    img:os03, title:'Security Shutters — Street-Facing Windows',  suburb:'Fullarton, Adelaide SA',    link:'/products/outdoor-shutters-adelaide' },
  // Linen Curtains (NEW)
  { id:22, cat:'Linen Curtains',      img:linen1, title:'Natural Linen Curtains — Living Room',     suburb:'Crafers, Adelaide Hills SA',link:'/products/linen-curtains-adelaide' },
  { id:23, cat:'Linen Curtains',      img:linen2, title:'Linen Drapes — Dining Area',               suburb:'Aldgate, Adelaide Hills SA',link:'/products/linen-curtains-adelaide' },
  { id:24, cat:'Linen Curtains',      img:linen3, title:'Textured Linen Curtains — Master Bedroom', suburb:'Belair, Adelaide SA',       link:'/products/linen-curtains-adelaide' },
  // Custom Curtains (NEW)
  { id:25, cat:'Custom Curtains',     img:custom1, title:'Bespoke Custom Curtains — Formal Living',  suburb:'Burnside, Adelaide SA',    link:'/products/custom-curtains-adelaide' },
  { id:26, cat:'Custom Curtains',     img:custom2, title:'Made-to-Measure Curtains — Open Plan',     suburb:'Norwood, Adelaide SA',     link:'/products/custom-curtains-adelaide' },
  { id:27, cat:'Custom Curtains',     img:custom3, title:'Designer Custom Curtains — Bedroom Suite', suburb:'North Adelaide SA',        link:'/products/custom-curtains-adelaide' },
];

const CATS = ['All','Sheer Curtains','Blockout Curtains','Blockout Blinds','Plantation Shutters','Outdoor Shutters','Linen Curtains','Custom Curtains'];

export default function Portfolio() {
  const [filter, setFilter] = useState('All');
  const [lb, setLb]         = useState(null); // { items, idx } — the grid or the featured project
  const lbRef               = useRef(null);
  const lbOpen              = lb !== null;

  const filtered = filter === 'All' ? portfolioItems : portfolioItems.filter(p => p.cat === filter);

  // Focus the lightbox when it opens so arrow keys / Escape work straight away
  useEffect(() => { if (lbOpen) lbRef.current?.focus(); }, [lbOpen]);

  const openLb  = (items, idx) => setLb({ items, idx });
  const closeLb = () => setLb(null);
  const lbStep  = dir => e => { e.stopPropagation(); setLb(s => ({ ...s, idx:(s.idx + dir + s.items.length) % s.items.length })); };
  const lbPrev  = lbStep(-1);
  const lbNext  = lbStep(1);
  const onKey   = e => { if(e.key==='ArrowLeft') lbPrev(e); if(e.key==='ArrowRight') lbNext(e); if(e.key==='Escape') closeLb(); };
  const current = lb && lb.items[lb.idx];

  return (
    <>
      <Helmet>
        <title>Our Work | Curtain, Blind & Shutter Installations Adelaide | YN Dream Curtains</title>
        <meta name="description" content={`Browse ${portfolioItems.length}+ real curtain, blind & shutter installations across Adelaide homes. See our completed projects — sheer curtains, blockout curtains, linen curtains, custom curtains, plantation shutters & more. Adelaide SA.`} />
        <link rel="canonical" href="https://yndreamcurtains.com/portfolio" />
        <meta property="og:title" content="Curtain & Blind Installations Adelaide | YN Dream Curtains Portfolio" />
        <meta property="og:description" content={`Browse ${portfolioItems.length}+ real curtain, blind and shutter installations across Adelaide homes.`} />
        <meta property="og:url" content="https://yndreamcurtains.com/portfolio" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <style>{`
        .portfolio-hero { background:var(--bg-warm); border-bottom:1px solid var(--border-soft); padding:120px 0 50px; }
        .filter-wrap { background:var(--bg-card); border-bottom:1px solid var(--border-soft); padding:18px 0; position:sticky; top:68px; z-index:100; box-shadow:var(--shadow-sm); }
        .filter-bar { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
        .filter-label { font-family:'Outfit',sans-serif; font-size:0.54rem; letter-spacing:0.24em; color:var(--text-dim); text-transform:uppercase; font-weight:500; white-space:nowrap; }
        .filter-btn { font-family:'Outfit',sans-serif; font-size:0.56rem; font-weight:600; letter-spacing:0.12em; text-transform:uppercase; color:var(--text-dim); background:var(--bg); border:1px solid var(--border-soft); padding:8px 14px; cursor:pointer; transition:all 0.3s; white-space:nowrap; }
        .filter-btn.active { color:#fff; background:var(--gold); border-color:var(--gold); }
        .filter-btn:hover:not(.active) { border-color:var(--gold-border); color:var(--gold); }
        .filter-count { font-family:'DM Sans',sans-serif; font-size:0.8rem; color:var(--text-dim); margin-left:auto; white-space:nowrap; }
        .filter-count span { color:var(--gold); font-weight:600; }
        .portfolio-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; padding:40px 0 80px; }
        .portfolio-item { position:relative; overflow:hidden; cursor:pointer; background:var(--bg-card); border:1px solid var(--border-soft); aspect-ratio:4/3; box-shadow:var(--shadow-sm); transition:all 0.4s; }
        .portfolio-item:hover { box-shadow:var(--shadow-lg); transform:translateY(-3px); }
        .portfolio-item img { width:100%; height:100%; object-fit:cover; display:block; transition:transform 0.7s; filter:brightness(0.88); }
        .portfolio-item:hover img { transform:scale(1.06); filter:brightness(0.97); }
        .portfolio-overlay { position:absolute; inset:0; background:linear-gradient(to top,rgba(28,24,18,0.88) 0%,rgba(28,24,18,0.1) 55%,transparent 100%); display:flex; flex-direction:column; justify-content:flex-end; padding:18px 20px; opacity:0; transition:opacity 0.4s; }
        .portfolio-item:hover .portfolio-overlay { opacity:1; }
        .portfolio-title { font-family:'Cormorant Garamond',serif; font-size:1.05rem; font-weight:600; color:#fff; margin-bottom:3px; }
        .portfolio-suburb { font-family:'DM Sans',sans-serif; font-size:0.76rem; color:rgba(201,168,76,0.9); }
        .portfolio-cat-badge { position:absolute; top:10px; left:10px; font-family:'Outfit',sans-serif; font-size:0.44rem; letter-spacing:0.18em; color:#fff; background:var(--gold); padding:4px 10px; text-transform:uppercase; font-weight:600; }
        .portfolio-new-badge { position:absolute; top:10px; right:10px; font-family:'Outfit',sans-serif; font-size:0.44rem; letter-spacing:0.18em; color:var(--gold); background:rgba(250,250,248,0.95); border:1px solid var(--gold-border); padding:3px 9px; text-transform:uppercase; font-weight:700; }
        .zoom-btn { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%) scale(0.7); color:var(--gold); opacity:0; background:rgba(250,250,248,0.92); border:1px solid var(--gold-border); width:48px; height:48px; border-radius:50%; display:flex; align-items:center; justify-content:center; transition:all 0.3s; }
        .portfolio-item:hover .zoom-btn { opacity:1; transform:translate(-50%,-50%) scale(1); }
        .portfolio-view-product { position:absolute; bottom:12px; right:12px; font-family:'Outfit',sans-serif; font-size:0.46rem; font-weight:700; letter-spacing:0.14em; text-transform:uppercase; color:#fff; background:var(--gold); padding:6px 12px; text-decoration:none; display:inline-flex; align-items:center; gap:5px; opacity:0; transform:translateY(8px); transition:all 0.3s; z-index:2; }
        .portfolio-item:hover .portfolio-view-product { opacity:1; transform:translateY(0); }
        .portfolio-view-product:hover { background:var(--text); }
        /* Lightbox */
        .lightbox { position:fixed; inset:0; background:rgba(28,24,18,0.97); z-index:9000; display:flex; align-items:center; justify-content:center; backdrop-filter:blur(16px); animation:fadeIn 0.25s; outline:none; }
        .lightbox-img { max-width:84vw; max-height:80vh; object-fit:contain; border:1px solid rgba(255,255,255,0.12); box-shadow:0 0 80px rgba(0,0,0,0.6); display:block; }
        .lightbox-close { position:absolute; top:20px; right:20px; background:rgba(250,250,248,0.95); border:none; color:var(--text); width:44px; height:44px; display:flex; align-items:center; justify-content:center; cursor:pointer; font-size:1.3rem; z-index:2; transition:all 0.3s; }
        .lightbox-close:hover { background:#fff; color:var(--gold); }
        .lightbox-nav { position:absolute; top:50%; transform:translateY(-50%); background:rgba(250,250,248,0.92); border:none; color:var(--text); width:52px; height:52px; display:flex; align-items:center; justify-content:center; cursor:pointer; font-size:1.6rem; z-index:2; transition:all 0.3s; user-select:none; }
        .lightbox-nav:hover { background:#fff; color:var(--gold); }
        .lightbox-nav.prev { left:16px; }
        .lightbox-nav.next { right:16px; }
        .lightbox-info { position:absolute; bottom:24px; left:50%; transform:translateX(-50%); text-align:center; background:rgba(250,250,248,0.96); border:1px solid var(--border-soft); padding:12px 30px; white-space:nowrap; box-shadow:var(--shadow-sm); }
        .lightbox-title { font-family:'Cormorant Garamond',serif; font-size:1.05rem; font-weight:600; color:var(--text); }
        .lightbox-sub { font-family:'DM Sans',sans-serif; font-size:0.78rem; color:var(--text-dim); margin-top:2px; }
        .lightbox-counter { font-family:'Outfit',sans-serif; font-size:0.5rem; letter-spacing:0.2em; color:var(--text-light); margin-top:3px; text-transform:uppercase; }
        .portfolio-cta { padding:60px 0 80px; text-align:center; border-top:1px solid var(--border-soft); }
        /* Featured project */
        .fp { padding:72px 0 64px; background:var(--bg); }
        .fp-pill { font-size:0.5rem; letter-spacing:0.22em; font-weight:600; color:var(--gold); background:var(--gold-pale); border:1px solid var(--gold-border); padding:4px 10px; }
        .fp-top { display:grid; grid-template-columns:1.1fr 1fr; gap:56px; align-items:end; margin-bottom:34px; }
        .fp-title { font-size:clamp(2.6rem,6vw,4.8rem); font-weight:600; line-height:0.98; letter-spacing:-0.015em; margin:6px 0 12px; }
        .fp-title em { font-style:italic; font-weight:500; color:var(--gold); }
        .fp-tagline { font-family:'Cormorant Garamond',serif; font-style:italic; font-size:1.3rem; color:var(--text-mid); line-height:1.4; }
        .fp-meta { display:flex; flex-wrap:wrap; gap:20px; margin-top:16px; font-family:'Outfit',sans-serif; font-size:0.58rem; letter-spacing:0.2em; text-transform:uppercase; color:var(--text-dim); font-weight:500; }
        .fp-meta span { display:inline-flex; align-items:center; gap:7px; }
        .fp-meta svg { color:var(--gold); }
        .fp-story { font-size:1rem; font-weight:300; color:var(--text-mid); line-height:1.8; padding-left:24px; border-left:1px solid var(--gold-border); }
        .fp-mosaic { display:grid; grid-template-columns:1.62fr 1fr; grid-template-rows:1fr 1fr; gap:14px; height:clamp(420px,46vw,560px); }
        .fp-shot { position:relative; display:block; width:100%; height:100%; padding:0; border:none; overflow:hidden; cursor:pointer; background:var(--bg-deep); box-shadow:var(--shadow-sm); transition:box-shadow 0.4s; }
        .fp-shot:first-child { grid-row:1 / span 2; }
        .fp-shot:hover { box-shadow:var(--shadow-lg); }
        .fp-shot:focus-visible { outline:2px solid var(--gold); outline-offset:3px; }
        .fp-shot img { width:100%; height:100%; object-fit:cover; display:block; transition:transform 1.2s cubic-bezier(0.2,0.7,0.2,1); }
        .fp-shot:hover img { transform:scale(1.05); }
        .fp-shot::after { content:''; position:absolute; inset:0; z-index:1; pointer-events:none; background:linear-gradient(to top,rgba(28,24,18,0.62) 0%,rgba(28,24,18,0) 42%); }
        .fp-shot-cap { position:absolute; left:20px; bottom:16px; z-index:2; display:flex; align-items:baseline; gap:10px; font-family:'Outfit',sans-serif; font-size:0.6rem; letter-spacing:0.2em; text-transform:uppercase; color:#fff; font-weight:500; text-align:left; }
        .fp-shot-cap em { font-family:'Cormorant Garamond',serif; font-size:1.35rem; letter-spacing:0; color:var(--gold-light); }
        .fp-shot-zoom { position:absolute; top:14px; right:14px; z-index:2; width:36px; height:36px; border-radius:50%; background:rgba(250,250,248,0.92); color:var(--gold); display:flex; align-items:center; justify-content:center; opacity:0; transform:scale(0.8); transition:all 0.3s; }
        .fp-shot:hover .fp-shot-zoom, .fp-shot:focus-visible .fp-shot-zoom { opacity:1; transform:scale(1); }
        .fp-drape { position:absolute; top:0; bottom:0; width:50.5%; z-index:3; pointer-events:none; background:repeating-linear-gradient(90deg,#F4EEE4 0px,#E3D7C3 12px,#F4EEE4 24px); transition:transform 1.4s cubic-bezier(0.77,0,0.18,1); }
        .fp-drape.l { left:0; box-shadow:inset -14px 0 18px -14px rgba(100,80,40,0.35); }
        .fp-drape.r { right:0; box-shadow:inset 14px 0 18px -14px rgba(100,80,40,0.35); }
        .visible .fp-drape.l { transform:translateX(-101%); }
        .visible .fp-drape.r { transform:translateX(101%); }
        .fp-specs { display:flex; align-items:center; margin-top:28px; border-top:1px solid var(--border-soft); border-bottom:1px solid var(--border-soft); }
        .fp-spec { flex:1 1 0; min-width:0; padding:20px 22px; border-right:1px solid var(--border-soft); }
        .fp-spec:first-child { padding-left:0; }
        .fp-spec-label { font-family:'Outfit',sans-serif; font-size:0.52rem; letter-spacing:0.26em; text-transform:uppercase; color:var(--text-dim); font-weight:500; margin-bottom:6px; }
        .fp-spec-value { font-family:'Cormorant Garamond',serif; font-size:1.18rem; font-weight:600; color:var(--text); line-height:1.25; }
        .fp-cta { margin-left:26px; }
        @media(max-width:860px){
          .fp { padding:52px 0 44px; }
          .fp-top { grid-template-columns:1fr; gap:22px; margin-bottom:26px; }
          .fp-mosaic { grid-template-columns:1fr 1fr; grid-template-rows:none; height:auto; gap:10px; }
          .fp-shot { aspect-ratio:4/3; }
          .fp-shot:first-child { grid-column:1 / -1; grid-row:auto; }
          .fp-specs { display:grid; grid-template-columns:1fr 1fr; column-gap:16px; border-bottom:none; }
          .fp-spec { padding:16px 0; border-right:none; border-bottom:1px solid var(--border-soft); }
          .fp-cta { grid-column:1 / -1; margin:22px 0 0; justify-content:center; }
        }
        @media(max-width:500px){ .fp-shot-cap { left:12px; bottom:10px; font-size:0.5rem; } .fp-shot-cap em { font-size:1.1rem; } }
        @media(prefers-reduced-motion:reduce){ .fp-drape { display:none; } .fp-shot img { transition:none; } }
        @media(max-width:860px){ .portfolio-grid{grid-template-columns:repeat(2,1fr);gap:10px;} }
        @media(max-width:500px){ .portfolio-grid{grid-template-columns:1fr;} .filter-btn{font-size:0.5rem;padding:7px 10px;} }
        @media(max-width:600px){ .lightbox-nav.prev{left:4px;} .lightbox-nav.next{right:4px;} .lightbox-img{max-width:94vw;} }
      `}</style>

      {/* Page Hero */}
      <div className="portfolio-hero">
        <div className="container">
          <AnimatedSection>
            <div className="section-label">Our Work</div>
            <h1 className="section-title" style={{marginBottom:12}}>Curtain & Blind Installations <em>Adelaide</em></h1>
            <p className="section-desc">Browse {portfolioItems.length} real completed curtain, blind and shutter installations across Adelaide — every photo from an actual project by our team.</p>
          </AnimatedSection>
        </div>
      </div>

      {/* Featured project */}
      <section className="fp" aria-labelledby="fp-title">
        <div className="container">
          <AnimatedSection>
            <div className="fp-top">
              <div>
                <div className="section-label">Featured Project <span className="fp-pill">Just Completed</span></div>
                <h2 id="fp-title" className="fp-title">{featuredProject.heading}</h2>
                <p className="fp-tagline">{featuredProject.tagline}</p>
                <div className="fp-meta">
                  <span><MapPin size={12}/> {featuredProject.suburb}</span>
                  <span><Camera size={12}/> {featuredPhotos.length} photos</span>
                </div>
              </div>
              <p className="fp-story">{featuredProject.story}</p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={120}>
            <div className="fp-mosaic">
              {featuredPhotos.map((p,i)=>(
                <button key={p.id} type="button" className="fp-shot" onClick={()=>openLb(featuredPhotos,i)} aria-label={`Enlarge photo: ${p.title}`}>
                  <img src={p.img} alt={p.title} loading="lazy" />
                  {/* Two fabric panels that part like curtains when the section scrolls into view */}
                  <span className="fp-drape l" style={{transitionDelay:`${350+i*180}ms`}} aria-hidden="true"/>
                  <span className="fp-drape r" style={{transitionDelay:`${350+i*180}ms`}} aria-hidden="true"/>
                  <span className="fp-shot-cap"><em>0{i+1}</em>{p.room}</span>
                  <span className="fp-shot-zoom" aria-hidden="true"><ZoomIn size={16}/></span>
                </button>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <div className="fp-specs">
              {featuredProject.specs.map(s=>(
                <div key={s.label} className="fp-spec">
                  <div className="fp-spec-label">{s.label}</div>
                  <div className="fp-spec-value">{s.value}</div>
                </div>
              ))}
              <Link to={featuredProject.link} className="btn-primary fp-cta">Explore {featuredProject.name} <ArrowRight size={13}/></Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Sticky filter bar */}
      <div className="filter-wrap">
        <div className="container">
          <div className="filter-bar">
            <span className="filter-label">Filter:</span>
            {CATS.map(c=>(
              <button key={c} className={`filter-btn${filter===c?' active':''}`} onClick={()=>setFilter(c)}>{c}</button>
            ))}
            <span className="filter-count"><span>{filtered.length}</span> {filtered.length===1?'project':'projects'}</span>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="container">
        <div className="portfolio-grid">
          {filtered.map((item,i)=>(
            <AnimatedSection key={item.id} delay={i*30}>
              <div className="portfolio-item" onClick={()=>openLb(filtered,i)}>
                <img src={item.img} alt={item.title} loading="lazy" width="400" height="300" />
                <span className="portfolio-cat-badge">{item.cat}</span>
                {item.isNew && <span className="portfolio-new-badge">New</span>}
                <div className="zoom-btn"><ZoomIn size={20}/></div>
                <div className="portfolio-overlay">
                  <div className="portfolio-title">{item.title}</div>
                  <div className="portfolio-suburb">{item.suburb}</div>
                </div>
                <Link to={item.link} className="portfolio-view-product" onClick={e=>e.stopPropagation()}>
                  View Product <ArrowRight size={10}/>
                </Link>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="portfolio-cta">
        <div className="container">
          <AnimatedSection>
            <div className="section-label" style={{justifyContent:'center'}}>Ready to Transform Your Home?</div>
            <h2 className="section-title" style={{textAlign:'center',marginBottom:16}}>Get Your <em>Free Quote</em></h2>
            <p className="section-desc" style={{textAlign:'center',maxWidth:520,margin:'0 auto 32px'}}>
              Love what you see? Our Adelaide team will visit your home, measure your windows, and provide a personalised quote — completely free.
            </p>
            <div style={{display:'flex',gap:14,justifyContent:'center',flexWrap:'wrap'}}>
              <Link to="/book-online" className="btn-primary">Book Free Consultation <ArrowRight size={13}/></Link>
              <Link to="/products" className="btn-secondary">Browse All Products</Link>
            </div>
          </AnimatedSection>
        </div>
      </div>

      {/* Lightbox */}
      {current && (
        <div className="lightbox" ref={lbRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={current.title} onKeyDown={onKey} onClick={closeLb}>
          <button className="lightbox-close" onClick={closeLb} aria-label="Close lightbox">✕</button>
          {lb.items.length>1&&<>
            <button className="lightbox-nav prev" onClick={lbPrev} aria-label="Previous image">‹</button>
            <button className="lightbox-nav next" onClick={lbNext} aria-label="Next image">›</button>
          </>}
          <img src={current.img} alt={current.title} className="lightbox-img" onClick={e=>e.stopPropagation()}/>
          <div className="lightbox-info" onClick={e=>e.stopPropagation()}>
            <div className="lightbox-title">{current.title}</div>
            <div className="lightbox-sub">{current.suburb} · {current.cat}</div>
            {lb.items.length>1&&<div className="lightbox-counter">{lb.idx+1} / {lb.items.length}</div>}
          </div>
        </div>
      )}

      {/* ImageGallery Schema for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html:JSON.stringify({
        "@context":"https://schema.org",
        "@type":"ImageGallery",
        "name":"YN Dream Curtains — Adelaide Installation Portfolio",
        "description":`Browse ${portfolioItems.length}+ real curtain, blind and shutter installations across Adelaide homes by YN Dream Curtains.`,
        "url":"https://yndreamcurtains.com/portfolio",
        "numberOfItems": portfolioItems.length,
        "about":{
          "@type":"LocalBusiness",
          "name":"YN Dream Curtains",
          "areaServed":"Adelaide, South Australia"
        }
      })}} />
    </>
  );
}
